import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { KPI } from '@heroui-pro/react/kpi';
import { KPIGroup, Widget } from '@heroui-pro/react';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    PURCHASE_SUMMARY_ITEM_ICON,
    PURCHASE_SUMMARY_ITEM_LABEL_KEY,
    PURCHASE_SUMMARY_ITEM_ORDER,
    type PurchaseSummary,
    getPurchaseSummaryItemValue,
    isPurchaseSummaryItemMonetary,
} from './purchaseSummaryMeta';

type PurchaseSummaryViewProps = {
    data: PurchaseSummary;
};

/**
 * Four-tile KPI strip for the Purchases page. Mirrors the structure of
 * `RevenueSummaryView` so the two summary widgets share visual rhythm:
 * a `Widget` shell with a single `KPIGroup` underneath, items separated
 * by `KPIGroup.Separator`. No trend chips here — the underlying numbers
 * are inventory operations counts, not period-over-period revenue.
 */
export const PurchaseSummaryView: React.FC<PurchaseSummaryViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.purchaseSummary.title'),
        }),
        [t],
    );

    return (
        <section
            aria-labelledby="purchase-summary-widget-title"
            {...getNodeTestIdProp('purchase-summary-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('purchase-summary-widget')}
            >
                <Widget.Header
                    {...getNodeTestIdProp('purchase-summary-widget-header')}
                >
                    <Widget.Title
                        {...getNodeTestIdProp('purchase-summary-widget-title')}
                    >
                        {copy.title}
                    </Widget.Title>
                </Widget.Header>
                <Widget.Content
                    {...getNodeTestIdProp('purchase-summary-widget-content')}
                >
                    <KPIGroup className="bg-transparent shadow-none flex-col sm:flex-row">
                        {PURCHASE_SUMMARY_ITEM_ORDER.map((key, index) => {
                            const value = getPurchaseSummaryItemValue(
                                data,
                                key,
                            );
                            const isMonetary =
                                isPurchaseSummaryItemMonetary(key);

                            return (
                                <React.Fragment key={key}>
                                    <KPI
                                        {...getNodeTestIdProp(
                                            `purchase-summary-item-${key}`,
                                        )}
                                    >
                                        <KPI.Header
                                            {...getNodeTestIdProp(
                                                `purchase-summary-item-header-${key}`,
                                            )}
                                        >
                                            <KPI.Icon
                                                {...getNodeTestIdProp(
                                                    `purchase-summary-item-icon-wrap-${key}`,
                                                )}
                                            >
                                                <Icon
                                                    className="size-4"
                                                    {...getNodeTestIdProp(
                                                        `purchase-summary-item-icon-${key}`,
                                                    )}
                                                    icon={
                                                        PURCHASE_SUMMARY_ITEM_ICON[
                                                            key
                                                        ]
                                                    }
                                                />
                                            </KPI.Icon>
                                            <KPI.Title
                                                {...getNodeTestIdProp(
                                                    `purchase-summary-item-title-${key}`,
                                                )}
                                            >
                                                {t(
                                                    PURCHASE_SUMMARY_ITEM_LABEL_KEY[
                                                        key
                                                    ],
                                                )}
                                            </KPI.Title>
                                        </KPI.Header>
                                        <KPI.Content
                                            {...getNodeTestIdProp(
                                                `purchase-summary-item-content-${key}`,
                                            )}
                                        >
                                            {isMonetary ? (
                                                // BOS-162: the render prop is
                                                // how a HeroUI NumberValue
                                                // yields to the app's single
                                                // money format. `value` is
                                                // still passed so the component
                                                // keeps its own semantics.
                                                <KPI.Value
                                                    {...getNodeTestIdProp(
                                                        `purchase-summary-item-value-${key}`,
                                                    )}
                                                    value={value}
                                                >
                                                    {() => formatGel(value)}
                                                </KPI.Value>
                                            ) : (
                                                <KPI.Value
                                                    {...getNodeTestIdProp(
                                                        `purchase-summary-item-value-${key}`,
                                                    )}
                                                    maximumFractionDigits={0}
                                                    minimumFractionDigits={0}
                                                    style="decimal"
                                                    value={value}
                                                />
                                            )}
                                        </KPI.Content>
                                    </KPI>
                                    {index <
                                    PURCHASE_SUMMARY_ITEM_ORDER.length - 1 ? (
                                        <KPIGroup.Separator
                                            className="h-px w-full sm:h-auto sm:w-px"
                                            {...getNodeTestIdProp(
                                                `purchase-summary-item-separator-${key}`,
                                            )}
                                        />
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </KPIGroup>
                </Widget.Content>
            </Widget>
        </section>
    );
};
